import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { BRAND } from '../config/brand';

const Register = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accountType, setAccountType] = useState('profile');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Use the AuthContext register helper so token/user are handled consistently
      const username = (data.username || '').replace(/\s+/g, ' ').trim();
      const result = await registerUser(data.email, data.password, username, accountType);

      if (result?.success) {
        toast.success('Аккаунт создан. Проверьте почту для кода подтверждения.');
        // Navigate to email verification page
        navigate('/verify-email', {
          state: { email: data.email, accountType },
          replace: true
        });
      } else {
        const errMsg = result?.error || 'Ошибка при создании аккаунта';
        toast.error(errMsg);
      }
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.error || 'Ошибка при создании аккаунта';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">a</span>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-secondary-900">
            Создать аккаунт
          </h2>
          <p className="mt-2 text-secondary-600">
            Или{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
              войдите в существующий аккаунт
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div className="p-4 rounded-xl border border-secondary-200 bg-white shadow-soft">
              <div className="text-sm font-semibold text-secondary-900">Тип регистрации</div>
              <div className="mt-3 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="accountType"
                    value="profile"
                    checked={accountType === 'profile'}
                    onChange={() => setAccountType('profile')}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300"
                  />
                  <span>
                    <div className="text-sm font-medium text-secondary-900">Хочу создать профиль</div>
                    <div className="text-xs text-secondary-600">Будет доступна страница профиля и настройки.</div>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="accountType"
                    value="guest"
                    checked={accountType === 'guest'}
                    onChange={() => setAccountType('guest')}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300"
                  />
                  <span>
                    <div className="text-sm font-medium text-secondary-900">Зарегистрироваться как гость</div>
                    <div className="text-xs text-secondary-600">Без публичного профиля (можно апгрейднуть позже).</div>
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-secondary-700 mb-2">
                Имя пользователя
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                <input
                  id="username"
                  type="text"
                  {...register('username', {
                    required: 'Имя пользователя обязательно',
                    minLength: {
                      value: 3,
                      message: 'Имя пользователя должно содержать минимум 3 символа'
                    },
                    maxLength: {
                      value: 30,
                      message: 'Имя пользователя не должно превышать 30 символов'
                    },
                    pattern: {
                      value: /^[\p{L}\p{N}_\- ]+$/u,
                      message: 'Имя пользователя может содержать буквы (включая кириллицу), цифры, пробелы, дефисы и подчёркивания'
                    }
                  })}
                  className="input pl-10"
                  placeholder="username"
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-orange-600">{errors.username.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                <input
                  id="email"
                  type="email"
                  {...register('email', {
                    required: 'Email обязателен',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Неверный формат email'
                    }
                  })}
                  className="input pl-10"
                  placeholder="your@email.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-orange-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-2">
                Пароль
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: 'Пароль обязателен',
                    minLength: {
                      value: 6,
                      message: 'Пароль должен содержать минимум 6 символов'
                    }
                  })}
                  className="input pl-10 pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-orange-600">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-secondary-700 mb-2">
                Подтвердите пароль
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...register('confirmPassword', {
                    required: 'Подтверждение пароля обязательно',
                    validate: value => value === password || 'Пароли не совпадают'
                  })}
                  className="input pl-10 pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-orange-600">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              {...register('terms', {
                required: 'Вы должны согласиться с условиями'
              })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
            />
            <label htmlFor="terms" className="ml-2 block text-sm text-secondary-700">
              Я согласен с{' '}
              <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                условиями использования
              </Link>
              {' '}и{' '}
              <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                политикой конфиденциальности
              </Link>
            </label>
          </div>
          {errors.terms && (
            <p className="text-sm text-orange-600">{errors.terms.message}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Создание аккаунта...
              </>
            ) : (
              'Создать аккаунт'
            )}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-secondary-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-secondary-50 text-secondary-500">Или зарегистрируйтесь через</span>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={() => window.location.href = `${process.env.REACT_APP_API_URL || BRAND.baseUrl}/api/auth/yandex`}
              className="btn btn-secondary w-full flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.4 2.096C10.746 2.096 8.6 4.242 8.6 6.896c0 1.087.354 2.087.95 2.9L2.096 13.4C.95 13.4 0 14.35 0 15.5s.95 2.1 2.096 2.1c1.146 0 2.096-.95 2.096-2.1 0-.354-.087-.687-.242-.996l7.454-3.604c.354.596.95.996 1.596.996.55 0 1-.45 1-1s-.45-1-1-1c-.354 0-.687.2-.85.5L7.2 8.896C7.2 8.896 7.2 8.896 7.2 8.896c-.146-.3-.242-.65-.242-1 0-1.65 1.35-3 3-3s3 1.35 3 3c0 .35-.096.7-.242 1l-3.604 7.454c.596.354.996.95.996 1.596 0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2c-.354 0-.687.2-.85.5L3.604-7.454c.3-.146.5-.5.5-.85 0-.55-.45-1-1-1s-1 .45-1 1c0 .354.2.687.5.85l7.454 3.604c-.354.596-.95.996-1.596.996-1.65 0-3-1.35-3-3z" />
              </svg>
              <span>Yandex</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
